<template>
  <section>
    <v-row>
      <!-- Reminder for international delivery -->
      <v-col v-if="note !== ''" cols="12">
        <v-alert type="warning" icon="mdi-alert-circle" class="mb-0">
          {{ note }}
        </v-alert>
      </v-col>

      <!-- Start date picker -->
      <v-col cols="12" md="6">
        <h3>{{ $translate('DATE') }}</h3>
        <v-menu
          ref="menuStartDate"
          v-model="menuStartDate"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="computedDateFormattedStartDate"
              v-bind="attrs"
              v-on="on"
              readonly
              outlined
              ref="menuStartDate"
              append-icon="today"
              hide-details="auto"
              :rules="requiredRules"
            />
          </template>
          <v-date-picker
            v-model="startDate"
            no-title
            event-color="primary"
            :allowed-dates="allowedStartDates"
            :events="allowedStartDates"
            scrollable
            @input="onStartDateChange"
          />
        </v-menu>
      </v-col>

      <!-- Start time picker -->
      <v-col cols="12" md="6">
        <h3>{{ $translate('TIME') }}</h3>
        <v-select
          v-model="startTime"
          :items="startTimeList"
          hide-details="auto"
          outlined
          :rules="requiredRules"
          @input="onStartTimeChange"
        >
          <template slot="selection" slot-scope="data">
            {{ data.item.StartTime }}
            <!-- - {{ data.item.EndTime }} -->
          </template>
          <template slot="item" slot-scope="data">
            <template>
              <v-row justify="space-between" class="py-1">
                {{ data.item.StartTime + ' - ' + data.item.EndTime }}
                <div class="text-xs-center">
                  <v-chip
                    :dark="getChipData('darkModus', data.item)"
                    :style="`background-color: ${getChipData(
                      'color',
                      data.item
                    )}`"
                  >
                    {{ $translate(getChipData('text', data.item)) }}
                  </v-chip>
                </div>
              </v-row>
            </template>
          </template>
        </v-select>
      </v-col>

      <!-- End date picker -->
      <v-col v-if="endDateList.length > 0" cols="12" md="6">
        <v-menu
          ref="menuEndDate"
          v-model="menuEndDate"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="computedDateFormattedEndDate"
              :label="$translate('PLEASE_ENDDATE')"
              v-bind="attrs"
              v-on="on"
              readonly
              outlined
              :disabled="endDateList.length < 2"
              append-icon="today"
              hide-details="auto"
              :rules="requiredRules"
            />
          </template>
          <v-date-picker
            v-model="endDate"
            no-title
            event-color="primary"
            :allowed-dates="allowedEndDates"
            :events="allowedEndDates"
            @input="onEndDateChange"
            scrollable
          />
        </v-menu>
      </v-col>

      <!-- End time picker -->
      <v-col v-if="endTimeList.length > 0" cols="12" md="6">
        <v-select
          v-model="endTime"
          :label="$translate('PLEASE_ENDTIME')"
          :items="endTimeList"
          :disabled="endTimeList.length < 2"
          hide-details="auto"
          outlined
          :rules="requiredRules"
          @input="onEndTimeChange"
        >
          <template slot="selection" slot-scope="data">
            {{ data.item.StartTime }}
            <!-- - {{ data.item.EndTime }} -->
          </template>
          <template slot="item" slot-scope="data">
            <template>
              <v-row justify="space-between" class="py-1">
                {{ data.item.StartTime + ' - ' + data.item.EndTime }}
                <div class="text-xs-center">
                  <v-chip
                    :dark="getChipData('darkModus', data.item)"
                    :style="`background-color: ${getChipData(
                      'color',
                      data.item
                    )}`"
                  >
                    {{ $translate(getChipData('text', data.item)) }}
                  </v-chip>
                </div>
              </v-row>
            </template>
          </template>
        </v-select>
      </v-col>

      <!-- Error message -->
      <v-col v-if="hasError" cols="12">
        <v-alert type="error" dense text class="mb-0">
          {{ errorMsg }}
        </v-alert>
      </v-col>
    </v-row>
  </section>
</template>

<script>
import doRequest from '@/api/dssRequest';
import utility from '@/shared/utility';
import { requiredRules } from '@/shared/validations_rules';

export default {
  name: 'DeliveryPickUpDateTime',
  components: {},
  props: {
    event: Object,
    note: String,
    deliveryDateTimeInfo: Object,

    // Optional
    orders: Array,

    /*
    Optional
    [
      {
        ProductId: 'xxx',
        ItemId: 'xxx',
        Quantity: 1,
      }
    ]
    */
    products: Array,
    subscriptionProducts: Array,

    // Initial selections
    initialStartDate: String,
    initialStartTime: Object,
    initialEndDate: String,
    initialEndTime: Object,
  },
  data() {
    return {
      menuStartDate: '',
      menuEndDate: '',
      startDateList: [],
      startTimeList: [],
      endDateList: [],
      endTimeList: [],
      ticketList: [],
      startDate: '',
      startTime: {},
      endDate: '',
      endTime: {},
      hasError: false,
      errorMsg: '',
      requiredRules,
    };
  },
  computed: {
    computedDateFormattedStartDate() {
      return this.formatDate(this.startDate);
    },
    computedDateFormattedEndDate() {
      return this.formatDate(this.endDate);
    },
    isDelivery() {
      return (
        this.event != null &&
        (this.event.Type === 'Delivery' || this.event.Type === 'Shiplee')
      );
    },
  },
  watch: {
    event() {
      this.getStartDates();
    },
    deliveryDateTimeInfo() {
      this.initDeliveryDateTime();
    },
  },
  mounted() {
    this.getStartDates();
  },
  methods: {
    allowedStartDates(val) {
      return this.startDateList.indexOf(val) !== -1;
    },
    allowedEndDates(val) {
      return this.endDateList.indexOf(val) !== -1;
    },
    getChipData(sendedInfo, item) {
      switch (sendedInfo) {
        case 'text':
          if (item.Occupancy < 0.25) return 'NOT_BUSY'; // green
          if (item.Occupancy < 0.5) return 'NORMAL'; // yellow
          return 'ALMOST_FULL'; // red
        case 'color':
          if (item.State === 0) return 'rgba(25, 181, 254, 1)'; // blue
          if (item.Occupancy < 0.25) return 'rgba(67, 160, 71, 1)'; // green
          if (item.Occupancy < 0.5) return 'rgba(240, 255, 0, 0.8)'; // yellow
          return 'rgba(240, 52, 52, 0.8)'; // red
        case 'darkModus':
          if (item.Occupancy < 0.25) return true;
          if (item.Occupancy < 0.5) return false;
          return true;
        default:
          return false;
      }
    },
    initDeliveryDateTime() {
      if (!this.isDelivery) return;

      this.startDateList = [];
      this.startTimeList = [];
      this.endDateList = [];
      this.endTimeList = [];
      const deliveryDateTimeInfo = this.deliveryDateTimeInfo;
      if (Object.keys(this.deliveryDateTimeInfo).length === 0) return;

      // Init lists
      this.initStartDateList(deliveryDateTimeInfo);
      this.initStartTimeList(deliveryDateTimeInfo);

      // Init selections
      this.initStartDate(deliveryDateTimeInfo);
      this.initStartTime(deliveryDateTimeInfo);
    },
    getStartDates() {
      if (this.event == null || this.event.EventId == null) {
        return;
      }

      if (this.isDelivery) return;

      this.startDateList = [];
      this.startTimeList = [];
      this.endDateList = [];
      this.endTimeList = [];

      const todayDate = utility.getCurrentDateDMY();

      const payload = {
        StartDate: todayDate,
        LocationId: '',
        Products: this.getPaylodProducts(this.products),
        SubscriptionProducts: this.getPaylodProducts(this.subscriptionProducts),
        OneClickBooking: true,
        Reservations: this.getPayloadReservations(),
      };

      this.clearError();

      doRequest(
        (response) => {
          if (
            response.data.StartDates == null ||
            response.data.StartDates.length === 0
          ) {
            // No start dates
            this.hasError = true;
            this.errorMsg = this.$translate('NO_START_DATES');
            return;
          }

          // Init lists
          this.initStartDateList(response.data);
          this.initEndDateList(response.data);
          this.initStartTimeList(response.data);
          this.initEndTimeList(response.data);

          // Init selections
          this.initStartDate(response.data);
          this.initEndDate(response.data);
          this.initStartTime(response.data);
          this.initEndTime(response.data);
        },
        () => {
          this.hasError = true;
          this.errorMsg = this.$translate('NO_START_DATES');
        },
        'getStartDates',
        this.event.EventId,
        payload
      );
    },
    getStartBatches() {
      this.startTimeList = [];
      this.endDateList = [];
      this.endTimeList = [];

      if (this.event == null || this.event.EventId == null) {
        return;
      }

      const payload = {
        StartDate: this.computedDateFormattedStartDate,
        LocationId: '',
        Products: this.getPaylodProducts(this.products),
        SubscriptionProducts: this.getPaylodProducts(this.subscriptionProducts),
        OneClickBooking: true,
        Reservations: this.getPayloadReservations(),
      };

      this.clearError();

      doRequest(
        (response) => {
          if (
            response.data.StartTimes == null ||
            response.data.StartTimes.length === 0
          ) {
            // No start dates
            this.hasError = true;
            this.errorMsg = this.$translate('NO_START_TIMES');
            return;
          }

          // Init lists
          this.initStartTimeList(response.data);
          this.initEndDateList(response.data);
          this.initEndTimeList(response.data);

          // Init selections
          this.initStartTime(response.data);
          this.initEndDate(response.data);
          this.initEndTime(response.data);

          if (response.data.Tickets != null) {
            this.$emit('setTicketList', response.data.Tickets);
          }
        },
        () => {
          this.hasError = true;
          this.errorMsg = this.$translate('NO_START_TIMES');
        },
        'getStartBatches',
        this.event.EventId,
        payload
      );
    },
    getEndDates() {
      this.endDateList = [];

      if (this.event == null || this.event.EventId == null) {
        return;
      }

      const payload = {
        PartnerEventId: this.event.EventId,
        StartDate: this.computedDateFormattedStartDate,
        StartTime: this.startTime.StartTime,
        LocationId: '',
        Products: this.getPaylodProducts(this.products),
        SubscriptionProducts: this.getPaylodProducts(this.subscriptionProducts),
        OneClickBooking: true,
        Reservations: this.getPayloadReservations(),
      };

      this.clearError();

      doRequest(
        (response) => {
          this.initEndDateList(response.data);

          if (response.data.EndDates != null) {
            this.endTimeList = response.data.EndTimes;
            this.endDate = this.formatDate(response.data.DefaultEndDate);
            this.endTime = response.data.DefaultEndTime;

            this.onEndDateChange();
            this.onEndTimeChange();
          }
        },
        () => {
          // this.error = true;
          // this.errorMessage = "Couldn't get end dates for this event...";
          // this.errorToRecallFunction = 'getEndDates';
        },
        'getEndDates',
        this.event.EventId,
        payload
      );
    },
    getPaylodProducts(productsArray) {
      const payloadProducts = [];

      if (productsArray != null) {
        productsArray.forEach((product) => {
          payloadProducts.push({
            ProductId: product.ProductId,
            ItemId: product.ItemId,
            Quantity: product.Quantity,
            OptionSelection: product.OptionSelection,
          });
        });
      }

      return payloadProducts;
    },
    getPayloadReservations() {
      const payloadReservations = {};

      if (this.orders != null) {
        this.orders.forEach((reservation) => {
          if (payloadReservations[reservation.EventId] == null) {
            payloadReservations[reservation.EventId] = [];
          }
          payloadReservations[reservation.EventId].push(reservation.OrderId);
        });
      }

      return payloadReservations;
    },
    formatDate(date) {
      if (!date) return '';
      const [year, month, day] = date.split('-');
      return `${day}-${month}-${year}`;
    },
    initStartDateList(response) {
      this.startDateList = [];
      if (response.StartDates != null) {
        response.StartDates.forEach((date) => {
          this.startDateList.push(this.formatDate(date));
        });
      }
    },
    initEndDateList(response) {
      this.endDateList = [];
      if (response.EndDates != null) {
        response.EndDates.forEach((date) => {
          this.endDateList.push(this.formatDate(date));
        });
      }
    },
    initStartTimeList(response) {
      this.startTimeList = [];
      if (response.StartTimes != null) {
        this.startTimeList = [...response.StartTimes];
      }
    },
    initEndTimeList(response) {
      this.endTimeList = [];
      if (response.EndTimes != null) {
        this.endTimeList = [...response.EndTimes];
      }
    },
    initStartDate(response) {
      this.startDate = '';
      if (this.startDateList == null || this.startDateList.length === 0) {
        return;
      }

      if (
        this.initialStartDate != null &&
        this.startDateList.includes(this.formatDate(this.initialStartDate))
      ) {
        this.startDate = this.formatDate(this.initialStartDate);
      } else if (response.DefaultStartDate != null) {
        this.startDate = this.formatDate(response.DefaultStartDate);
      } else {
        [this.startDate] = this.startDateList;
      }

      this.onStartDateChange();
    },
    initEndDate(response) {
      this.endDate = '';
      if (this.endDateList == null || this.endDateList.length === 0) {
        return;
      }

      if (
        this.initialEndDate != null &&
        this.endDateList.includes(this.formatDate(this.initialEndDate))
      ) {
        this.endDate = this.formatDate(this.initialEndDate);
      } else if (response.DefaultEndDate != null) {
        this.endDate = this.formatDate(response.DefaultEndDate);
      } else {
        [this.endDate] = this.endDateList;
      }

      this.onEndDateChange();
    },
    initStartTime(response) {
      if (this.startTimeList == null || this.startTimeList.length === 0) {
        return;
      }

      let timePopInList = null;
      if (
        this.initialStartTime != null &&
        this.initialStartTime.BatchGroupId != null
      ) {
        timePopInList = this.startTimeList.find(
          (time) =>
            time.BatchGroupId === this.initialStartTime.BatchGroupId &&
            time.StartTime === this.initialStartTime.StartTime
        );
      }

      if (timePopInList != null) {
        // Found initialStartTime in list
        this.startTime = { ...timePopInList };
      } else if (response.DefaultStartTime != null) {
        this.startTime = { ...response.DefaultStartTime };
      } else {
        [this.startTime] = this.startTimeList;
      }

      this.onStartTimeChange();
    },
    initEndTime(response) {
      this.endTime = {};
      if (this.endTimeList == null || this.endTimeList.length === 0) {
        return;
      }

      let timePopInList = null;
      if (
        this.initialEndTime != null &&
        this.initialEndTime.BatchGroupId != null
      ) {
        timePopInList = this.endTimeList.find(
          (time) =>
            time.BatchGroupId === this.initialEndTime.BatchGroupId &&
            time.StartTime === this.initialEndTime.StartTime
        );
      }

      if (timePopInList != null) {
        // Found initialEndTime in list
        this.endTime = { ...timePopInList };
      } else if (response.DefaultEndTime != null) {
        this.endTime = { ...response.DefaultEndTime };
      } else {
        [this.endTime] = this.endTimeList;
      }

      this.onEndTimeChange();
    },
    clearError() {
      this.hasError = false;
      this.errorMsg = '';
    },
    onStartDateChange() {
      this.$emit('start-date-change', this.computedDateFormattedStartDate);
      this.getStartBatches();
    },
    onStartTimeChange() {
      this.$emit('start-time-change', this.startTime);
      this.getEndDates();
    },
    onEndDateChange() {
      this.$emit('end-date-change', this.computedDateFormattedEndDate);
    },
    onEndTimeChange() {
      this.$emit('end-time-change', this.endTime);
    },
    onBack() {
      this.$emit('back');
    },
    onNext() {
      // this.$store.commit('subscription/setProducts', this.productsSelected);
      this.$emit('next');
    },
  },
};
</script>
